import React, { useState, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounce';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaUsers,
  FaCalendarAlt,
  FaFileExcel,
  FaFilter,
  FaUserCheck,
  FaFileAlt,
  FaCheckCircle,
  FaPause,
  FaTimes,
  FaHourglass,
  FaPlay,
  FaStop,
  FaBuilding,
  FaTasks,
  FaUser,
  FaClock,
  FaTimesCircle,
  FaCreditCard,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { FiFilter, FiRefreshCw, FiX, FiSearch as FiSearchIcon, FiUser, FiCalendar, FiMapPin, FiSettings } from 'react-icons/fi';
import { Pagination } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { SearchableSelect, SelectWithSearch } from '../../utils/select-with-search';
import type { SelectOption } from '../../utils/select-with-search';

import { useAuth } from '../../provider/auth.provider';
import { LuEye } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import api from '../../libs/axios';
import toast from 'react-hot-toast';

interface Client {
  id: number;
  client_name: string;
  status: string;
}

interface Activity {
  id: number;
  activity_name: string;
  status: string;
}

interface Project {
  id: number;
  project_name: string;
  status: string;
  client?: Client;
  activity?: Activity;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role_id: number;
}

interface Site {
  id: number;
  site_id: string;
  project_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  assigned_engineer_id?: number;
  assigned_ta_id?: number;
  assigned_report_maker_id?: number;
  assigned_validator_id?: number;
  visit_remarks?: string;
  cancel_reason?: string;
  hold_reason?: string;
  project?: Project;
  site_engineer?: User;
  ta?: User;
  mis?: User;
  report_designer?: User;
  validator?: User;
  survey_workflow_status: string;
  has_pending_billing?: boolean;
}

interface PaginationData {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface SortConfig {
  field: string;
  direction: 'ASC' | 'DESC';
}

interface BillingFilters {
  site_id?: string;
  project_id?: string;
  project_label?: string;
  activity_id?: string;
  activity_label?: string;
  state_circle_id?: string;
  district_id?: string;
  status?: string;
  site_priority?: string;
  technician_name?: string;
  technician_number?: string;
  fse_name?: string;
  fse_number?: string;
  aom_name?: string;
  aom_number?: string;
  client_name?: string;
  engineer_name?: string;
  technical_associate_name?: string;
  report_maker_name?: string;
  validator_name?: string;
  visit_remarks?: string;
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: BillingFilters;
  onFiltersChange: (filters: BillingFilters) => void;
  onApply: () => void;
  onReset: () => void;
  projects: Project[];
  activities: Activity[];
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  projects,
  activities,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'location' | 'dates' | 'status' | 'assignments'>('basic');
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);

  const projectOptions = useMemo<SelectOption[]>(
    () =>
      projects.map((project) => ({
        id: project.id,
        value: project.id,
        label: project.project_name || `Project #${project.id}`,
      })),
    [projects]
  );

  const activityOptions = useMemo<SelectOption[]>(
    () =>
      activities.map((activity) => ({
        id: activity.id,
        value: activity.id,
        label: activity.activity_name || `Activity #${activity.id}`,
      })),
    [activities]
  );

  useEffect(() => {
    if (isOpen) {
      setActiveTab('basic');
    }
  }, [isOpen]);

  useEffect(() => {
    if (filters.project_id) {
      const existingOption = projectOptions.find((option) => String(option.value) === filters.project_id);
      setSelectedProject(
        existingOption ||
        (filters.project_label
          ? {
            id: Number(filters.project_id),
            value: filters.project_id,
            label: filters.project_label,
          }
          : null)
      );
    } else {
      setSelectedProject(null);
    }
  }, [filters.project_id, filters.project_label, projectOptions]);

  useEffect(() => {
    if (filters.activity_id) {
      const existingOption = activityOptions.find((option) => String(option.value) === filters.activity_id);
      setSelectedActivity(
        existingOption ||
        (filters.activity_label
          ? {
            id: Number(filters.activity_id),
            value: filters.activity_id,
            label: filters.activity_label,
          }
          : null)
      );
    } else {
      setSelectedActivity(null);
    }
  }, [filters.activity_id, filters.activity_label, activityOptions]);

  useEffect(() => {
    if (!filters.state_circle_id) {
      setSelectedStateCircle(null);
    }
    if (!filters.district_id) {
      setSelectedDistrict(null);
    }
  }, [filters.state_circle_id, filters.district_id]);

  if (!isOpen) return null;

  const handleInputChange = (field: keyof BillingFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  const handleProjectSelect = (option: SelectOption | null) => {
    setSelectedProject(option);
    onFiltersChange({
      ...filters,
      project_id: option ? String(option.value) : undefined,
      project_label: option ? option.label : undefined,
    });
  };

  const handleActivitySelect = (option: SelectOption | null) => {
    setSelectedActivity(option);
    onFiltersChange({
      ...filters,
      activity_id: option ? String(option.value) : undefined,
      activity_label: option ? option.label : undefined,
    });
  };

  const handleStateCircleSelect = (option: SelectOption | null) => {
    setSelectedStateCircle(option);
    setSelectedDistrict(null);

    onFiltersChange({
      ...filters,
      state_circle_id: option ? option.value.toString() : undefined,
      district_id: undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {[{ id: 'basic', label: 'Basic Info', icon: FiSearchIcon }, { id: 'location', label: 'Location', icon: FiMapPin }, { id: 'assignments', label: 'Assignments', icon: FiUser }, { id: 'dates', label: 'Dates', icon: FiCalendar }, { id: 'status', label: 'Status', icon: FiSettings }].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as 'basic' | 'location' | 'dates' | 'status' | 'assignments')}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-500 text-blue-600 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., SITE123"
                  value={filters.site_id || ''}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                <SearchableSelect
                  value={selectedProject}
                  onChange={handleProjectSelect}
                  options={projectOptions}
                  placeholder="Search projects"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                <SearchableSelect
                  value={selectedActivity}
                  onChange={handleActivitySelect}
                  options={activityOptions}
                  placeholder="Search activities"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visit Remarks</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter remarks keyword"
                  value={filters.visit_remarks || ''}
                  onChange={(e) => handleInputChange('visit_remarks', e.target.value)}
                />
              </div> */}
            </div>
          )}

          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State Circles</label>
                <SelectWithSearch
                  placeholder="Select State Circle"
                  value={selectedStateCircle}
                  onChange={handleStateCircleSelect}
                  apiEndpoint="/state-circle/list"
                  dataKey="states"
                  labelKey="state_name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={selectedStateCircle ? Number(selectedStateCircle.value) : null}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Districts</label>
                <SelectWithSearch
                  placeholder="Select District"
                  value={selectedDistrict}
                  onChange={(option) => {
                    setSelectedDistrict(option);
                    onFiltersChange({
                      ...filters,
                      district_id: option ? option.value.toString() : undefined,
                    });
                  }}
                  apiEndpoint="/state-circle"
                  dataKey="districts"
                  labelKey="name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={selectedStateCircle ? Number(selectedStateCircle.value) : null}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.site_priority || ''}
                  onChange={(e) => handleInputChange('site_priority', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'assignments' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technician Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Technician Name"
                  value={filters.technician_name || ''}
                  onChange={(e) => handleInputChange('technician_name', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technician Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Technician Number"
                  value={filters.technician_number || ''}
                  onChange={(e) => handleInputChange('technician_number', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">FSE Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="FSE Name"
                  value={filters.fse_name || ''}
                  onChange={(e) => handleInputChange('fse_name', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">FSE Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="FSE Number"
                  value={filters.fse_number || ''}
                  onChange={(e) => handleInputChange('fse_number', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AOM Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="AOM Name"
                  value={filters.aom_name || ''}
                  onChange={(e) => handleInputChange('aom_name', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AOM Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="AOM Number"
                  value={filters.aom_number || ''}
                  onChange={(e) => handleInputChange('aom_number', e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.updated_from || ''}
                  onChange={(e) => handleInputChange('updated_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.updated_to || ''}
                  onChange={(e) => handleInputChange('updated_to', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingSites: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [users, setUsers] = useState<{ [roleId: number]: User[] }>({});

  const [pagination, setPagination] = useState<PaginationData>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<BillingFilters>({});
  const [filters, setFilters] = useState<BillingFilters>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'DESC',
  });

  // Debounced fetch — prevents API call on every keystroke
  const debouncedFetchSites = useDebouncedCallback(
    (value: string) => fetchSites(value, advancedFilters.status || '', advancedFilters.project_id || '', 1, undefined, sortConfig, advancedFilters),
    400
  );

  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean;
    type: 'engineer' | 'reportMaker' | 'validator' | null;
    site: Site | null;
  }>({ isOpen: false, type: null, site: null });

  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    type: 'cancel' | 'hold' | null;
    site: Site | null;
    reason: string;
  }>({ isOpen: false, type: null, site: null, reason: '' });

  const { hasPermission, user } = useAuth();

  useEffect(() => {
    setFilters(advancedFilters);
  }, [advancedFilters]);

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters).filter(([, value]) => value !== undefined && value !== '').length;

  const handleFiltersChange = (updatedFilters: BillingFilters) => {
    setFilters(updatedFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
    setAdvancedFilters({});
    setIsFilterModalOpen(false);
    fetchSites(searchTerm, '', '', 1, undefined, sortConfig, {});
  };

  const handleApplyFilters = () => {
    setAdvancedFilters(filters);
    setIsFilterModalOpen(false);
    fetchSites(searchTerm, filters.status || '', filters.project_id || '', 1, undefined, sortConfig, filters);
  };

  // API Functions
  const fetchSites = async (
    search: string = '',
    status: string = '',
    project_id: string = '',
    page: number = 1,
    limit?: number,
    sort: SortConfig = sortConfig,
    filtersParam: BillingFilters = advancedFilters
  ) => {
    setLoading(true);
    try {
      const queryParams: any = {
        page: page,
        limit: limit || pagination.limit || 10
      };

      if (search.trim()) {
        queryParams.search = search;
      }

      if (status.trim()) {
        queryParams.status = status;
      }

      if (project_id.trim()) {
        queryParams.project_id = project_id;
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field;
        queryParams.sort_order = sort.direction;
      }

      Object.entries(filtersParam || {}).forEach(([key, value]) => {
        if (value === undefined || value === '') return;
        if (key === 'status' || key === 'project_id') return;
        if (key.endsWith('_label')) return;
        queryParams[key] = value;
      });

      const response = await api.get('/survey/sites/pending-billing', {
        params: queryParams
      });

      if (response.data.success) {
        setSites(response.data.data.sites || []);
        setPagination(response.data.data.pagination || pagination);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects/list', {
        params: { limit: 100, status: 'ACTIVE' }
      });

      if (response.data.success) {
        setProjects(response.data.data.projects || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get('/activities/list', {
        params: { limit: 100, status: 'ACTIVE' }
      });

      if (response.data.success) {
        setActivities(response.data.data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchUsersByRole = async (roleId: number) => {
    try {
      const response = await api.get(`/survey/users/role/${roleId}`);

      if (response.data.success) {
        setUsers(prev => ({
          ...prev,
          [roleId]: response.data.data.users || []
        }));
      }
    } catch (error) {
      console.error('Error fetching users by role:', error);
    }
  };

  const assignEngineer = async (siteId: number, engineerId: number) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${siteId}/assign-engineer`, {
        assigned_engineer_id: engineerId
      });

      if (response.data.success) {
        await fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, sortConfig, advancedFilters);
        console.log('Engineer assigned successfully');
      }
    } catch (error) {
      console.error('Error assigning engineer:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const assignReportMaker = async (siteId: number, reportMakerId: number) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${siteId}/assign-report-maker`, {
        assigned_report_maker_id: reportMakerId
      });

      if (response.data.success) {
        await fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, sortConfig, advancedFilters);
        console.log('Report maker assigned successfully');
      }
    } catch (error) {
      console.error('Error assigning report maker:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const assignValidator = async (siteId: number, validatorId: number) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${siteId}/assign-validator`, {
        assigned_validator_id: validatorId
      });

      if (response.data.success) {
        await fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, sortConfig, advancedFilters);
        console.log('Validator assigned successfully');
      }
    } catch (error) {
      console.error('Error assigning validator:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelSite = async (siteId: number, cancelReason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${siteId}/cancel`, {
        cancel_reason: cancelReason
      });

      if (response.data.success) {
        await fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, sortConfig, advancedFilters);
        console.log('Site cancelled successfully');
      }
    } catch (error) {
      console.error('Error cancelling site:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const holdSite = async (siteId: number, holdReason: string) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/survey/sites/${siteId}/hold`, {
        hold_reason: holdReason
      });

      if (response.data.success) {
        await fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, sortConfig, advancedFilters);
        console.log('Site put on hold successfully');
      }
    } catch (error) {
      console.error('Error putting site on hold:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchSites();
    fetchProjects();
    fetchActivities();
    fetchUsersByRole(2); // Engineers
    fetchUsersByRole(5); // Report Makers
    fetchUsersByRole(6); // Validators
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFetchSites(value);
  };

  const handlePageChange = (page: number) => {
    fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', page, undefined, sortConfig, advancedFilters);
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', 1, newLimit, sortConfig, advancedFilters);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field: string) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    const newSort: SortConfig = { field, direction };
    setSortConfig(newSort);
    fetchSites(searchTerm, advancedFilters.status || '', advancedFilters.project_id || '', pagination.page, undefined, newSort, advancedFilters);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />;
    }
    return sortConfig.direction === 'ASC' ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      site_created: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Site Created'
      },
      engineer_assigned: {
        color: 'bg-blue-100 text-blue-800',
        icon: FaUser,
        text: 'Engineer Assigned'
      },
      field_work_in_progress: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FaClock,
        text: 'Field Work In Progress'
      },
      field_work_completed: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Field Work Completed'
      },
      data_uploaded: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Data Uploaded'
      },
      ta_review_pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FaClock,
        text: 'Technical Associate Review Pending'
      },
      ta_review_completed: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Technical Associate Review Completed'
      },
      ta_rejected: {
        color: 'bg-red-100 text-red-800',
        icon: FaTimesCircle,
        text: 'Technical Associate Review Rejected'
      },
      mis_report_assign_pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FaClock,
        text: 'MIS Report Assign Pending'
      },
      report_maker_assigned: {
        color: 'bg-blue-100 text-blue-800',
        icon: FaUser,
        text: 'Report Maker Assigned'
      },
      report_in_progress: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FaClock,
        text: 'Report In Progress'
      },
      report_completed: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Report Completed'
      },
      report_maker_rejected: {
        color: 'bg-red-100 text-red-800',
        icon: FaTimesCircle,
        text: 'Report Maker Rejected'
      },
      validator_assigned: {
        color: 'bg-blue-100 text-blue-800',
        icon: FaUser,
        text: 'Validator Assigned'
      },
      validation_in_progress: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FaClock,
        text: 'Validation In Progress'
      },
      validation_completed: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Validation Completed'
      },
      validation_rejected: {
        color: 'bg-red-100 text-red-800',
        icon: FaTimesCircle,
        text: 'Validation Rejected'
      },
      report_finalized: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Report Finalized'
      },
      report_sent_to_client: {
        color: 'bg-green-100 text-green-800',
        icon: FaCheckCircle,
        text: 'Report Sent To Client'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: 'bg-gray-100 text-gray-800',
      icon: FaMapMarkerAlt,
      text: status
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <config.icon className="w-2 h-2" />
        {config.text}
      </span>
    );
  };

  const canAssignEngineer = (site: Site) => {
    return hasPermission('Site', 'assign_engineer') &&
      ['site_unassign', 'po_pending'].includes(site.status);
  };

  const canAssignReportMaker = (site: Site) => {
    return hasPermission('Site', 'assign_report_maker') &&
      site.status === 'ta_done';
  };

  const canAssignValidator = (site: Site) => {
    return hasPermission('Site', 'assign_validator') &&
      site.status === 'report_done';
  };

  const canCancelSite = (site: Site) => {
    return hasPermission('Site', 'cancel') &&
      site.status !== 'report_submitted';
  };

  const canHoldSite = (site: Site) => {
    return hasPermission('Site', 'hold') &&
      !['report_submitted', 'cancelled'].includes(site.status);
  };

  const handleAssignment = (type: 'engineer' | 'reportMaker' | 'validator', site: Site) => {
    setAssignmentModal({ isOpen: true, type, site });
  };

  const handleAction = (type: 'cancel' | 'hold', site: Site) => {
    setActionModal({ isOpen: true, type, site, reason: '' });
  };

  const executeAssignment = (userId: number) => {
    if (!assignmentModal.site) return;

    switch (assignmentModal.type) {
      case 'engineer':
        assignEngineer(assignmentModal.site.id, userId);
        break;
      case 'reportMaker':
        assignReportMaker(assignmentModal.site.id, userId);
        break;
      case 'validator':
        assignValidator(assignmentModal.site.id, userId);
        break;
    }
    setAssignmentModal({ isOpen: false, type: null, site: null });
  };

  const executeAction = () => {
    if (!actionModal.site || !actionModal.reason.trim()) return;

    switch (actionModal.type) {
      case 'cancel':
        cancelSite(actionModal.site.id, actionModal.reason);
        break;
      case 'hold':
        holdSite(actionModal.site.id, actionModal.reason);
        break;
    }
    setActionModal({ isOpen: false, type: null, site: null, reason: '' });
  };

  const mapSiteForExport = (site: any) => {
    return {
      // Existing columns
      'Site ID': site.site_id,
      'Project': site.project?.project_name || 'N/A',
      'Client': site.project?.client?.client_name || 'N/A',
      'Activity': site.project?.activity?.activity_name || 'N/A',
      'Status': (site.status || '').replace(/_/g, ' '),
      'Engineer': site.site_engineer ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}` : 'Not Assigned',
      'Technical Associate': site.ta ? `${site.ta.first_name} ${site.ta.last_name}` : 'Not Assigned',
      'Report Maker': site.report_designer ? `${site.report_designer.first_name} ${site.report_designer.last_name}` : 'Not Assigned',
      'Validator': site.validator ? `${site.validator.first_name} ${site.validator.last_name}` : 'Not Assigned',
      'Created Date': formatDate(site.created_at),
      'Last Updated': formatDate(site.updated_at),
      // Additional non-PK columns from sample
      'Reference Number': site.reference_number || '',
      'Client Allocation Date': site.client_allocation_date || '',
      'Site Name': site.site_name || '',
      'Site Address': site.site_address || '',
      'Opco Name': site.opco_name || '',
      'Opco ID': site.opco_id || '',
      'State': site.state_circle?.state_name || '',
      'Circle': site.state_circle?.circle_name || '',
      'District': site.district?.name || '',
      'Pincode': site.pincode ?? '',
      'Site Latitude': site.site_latitude || '',
      'Site Longitude': site.site_longitude || '',
      'Site Priority': site.site_priority || '',
      'PR Number': site.pr_number || '',
      'PR Date': site.pr_date || '',
      'PO Number': site.po_number || '',
      'PO Date': site.po_date || '',
      'PO Shared At Allocation': site.is_po_no_shared_at_allocation ? 'Yes' : 'No',
      'Technician Name': site.technician_name || '',
      'Technician Number': site.technician_number || '',
      'FSE Name': site.fse_name || '',
      'FSE Number': site.fse_number || '',
      'AOM Name': site.aom_name || '',
      'AOM Number': site.aom_number || '',
      'Client Project Name': site.client_project_name || '',
      'Customer Requirement': site.customer_requirement || '',
      'Site Info 1': site.site_information1 || '',
      'Site Info 2': site.site_information2 || '',
      'Site Info 3': site.site_information3 || '',
      'Team Coordinator User ID': site.team_coordinator_userid ?? '',
      'Engineer Username': site.site_engineer_username || '',
      'Engineer Work Date': site.site_engineer_work_date || '',
      'Engineer Work Status': site.site_engineer_work_status || '',
      'Engineer Work Remarks': site.site_engineer_work_remarks || '',
      'Engineer Visit Status': site.site_engineer_visit_status || '',
      'TA Username': site.ta_username || '',
      'TA Work Date': site.ta_work_date || '',
      'TA Remarks': site.ta_remarks || '',
      'TA SE Quality Feedback': site.ta_se_quality_feedback || '',
      'TA Rating': site.ta_rating ?? '',
      'MIS Username': site.mis_username || '',
      'MIS Submission Date': site.mis_submission_date || '',
      'MIS Remarks': site.mis_remarks || '',
      'MIS Validator QA Feedback': site.mis_validator_qa_feedback || '',
      'Report Designer Username': site.report_designer_username || '',
      'Report Designer Work Date': site.report_designer_work_date || '',
      'Report Designer Remarks': site.report_designer_remarks || '',
      'Report Designer Rejection Reason': site.report_designer_rejection_reason || '',
      'Report Designer Quality Feedback': site.report_designer_quality_feedback || '',
      'Report Maker Priority': site.report_maker_priority || '',
      'Validator Username': site.validator_username || '',
      'Validator Work Date': site.validator_work_date || '',
      'Validator Remarks': site.validator_remarks || '',
      'Validator Report Designer QA Feedback': site.validator_report_designer_qa_feedback || '',
      'Validator Rating': site.validator_rating ?? '',
      'Validator Priority': site.validator_priority || '',
      'Contact Person': site.contact_person || '',
      'Contact Number': site.contact_number || '',
      'Survey Workflow Status': site.survey_workflow_status || '',
      'Cancel Reason': site.cancel_reason || '',
      'Hold Reason': site.hold_reason || '',
      'Is Location Verified': site.is_location_verified ? 'Yes' : 'No',
      'Location Verification Attempts': site.location_verification_attempts ?? '',
      'Last Location Check': site.last_location_check || '',
      'Source': site.source || '',
      // 'Source ID': site.source_id || '',
      'Project Name': site.project?.project_name || '',
      'Project Short Name': site.project?.project_short_name || '',
      'Client Full Name': site.project?.client?.client_full_name || '',
      'Client Short Name': site.project?.client?.client_short_name || '',
    };
  };

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number> = {
      page,
      limit,
    };

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }

    if (advancedFilters.status) {
      params.status = advancedFilters.status;
    }

    if (advancedFilters.project_id) {
      params.project_id = advancedFilters.project_id;
    }

    Object.entries(advancedFilters || {}).forEach(([key, value]) => {
      if (value === undefined || value === '' || key === 'status' || key === 'project_id' || key.endsWith('_label')) {
        return;
      }
      params[key] = value as string | number;
    });

    if (sortConfig.field) {
      params.sort_by = sortConfig.field;
      params.sort_order = sortConfig.direction;
    }

    return params;
  };

  const fetchExportData = async () => {
    if (exporting) return;

    try {
      setExporting(true);
      setExportData([]);

      const pageSize = 100;
      let page = 1;
      let hasMore = true;
      const allSites: any[] = [];

      while (hasMore) {
        const params = buildExportParams(page, pageSize);
        const response = await api.get('/survey/sites/pending-billing', { params });

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to fetch sites for export');
        }

        const data = response.data.data || {};
        const fetched: any[] = data.sites || [];
        const paginationInfo = data.pagination;

        allSites.push(...fetched);

        const nextPageAvailable =
          paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize);

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false;
        } else {
          page += 1;
        }
      }

      if (!allSites.length) {
        toast.error('No sites found for export');
        return;
      }

      setExportData(allSites.map(mapSiteForExport));
      setShowExcelModal(true);
    } catch (error: any) {
      console.error('Error exporting sites:', error);
      toast.error(error?.response?.data?.message || 'Failed to prepare export data');
    } finally {
      setExporting(false);
    }
  };

  const getAssignmentUsers = () => {
    switch (assignmentModal.type) {
      case 'engineer':
        return users[2] || [];
      case 'reportMaker':
        return users[5] || [];
      case 'validator':
        return users[6] || [];
      default:
        return [];
    }
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Billing Sites</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Sites</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by site ID or project name"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Status Filter moved to Advanced Filters */}
          {/**
           * <div className="w-full sm:w-48">
           *   <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
           *   <div className="relative">
           *     <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
           *     <select ...>
           *       ...
           *     </select>
           *   </div>
           * </div>
           */}

          {/* Project Filter moved to Advanced Filters */}
          {/**
           * <div className="w-full sm:w-48">
           *   <label className="block text-xs font-medium text-gray-700 mb-1">Project Filter</label>
           *   <div className="relative">
           *     <FaProjectDiagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
           *     <select ...>
           *       ...
           *     </select>
           *   </div>
           * </div>
           */}

          <div className="w-full sm:w-auto flex items-end">
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${getActiveFiltersCount() > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                Actions
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('site_id')}
                >
                  <span>Site ID</span>
                  {getSortIcon('site_id')}
                </button>
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                Project Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('engineer_name')}
                >
                  <span>Engineer</span>
                  {getSortIcon('engineer_name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('technical_associate_name')}
                >
                  <span>Technical Associate</span>
                  {getSortIcon('technical_associate_name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('report_maker_name')}
                >
                  <span>Report Maker</span>
                  {getSortIcon('report_maker_name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('validator_name')}
                >
                  <span>Validator</span>
                  {getSortIcon('validator_name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('survey_workflow_status')}
                >
                  <span>Status</span>
                  {getSortIcon('survey_workflow_status')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('visit_remarks')}
                >
                  <span>Visit Remarks</span>
                  {getSortIcon('visit_remarks')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('created_at')}
                >
                  <span>Created At</span>
                  {getSortIcon('created_at')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-600"
                  onClick={() => handleSort('updated_at')}
                >
                  <span>Updated At</span>
                  {getSortIcon('updated_at')}
                </button>
              </th>

            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={13} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading sites...</span>
                  </div>
                </td>
              </tr>
            ) : sites.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaMapMarkerAlt className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No sites found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                  </div>
                </td>
              </tr>
            ) : (
              sites.map((site) => (
                <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Link
                        target='_blank'
                        to={`/sites/${site.id}`}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                        title="Site ID"
                      >
                        <LuEye className="w-4 h-4" />
                      </Link>

                      {site.has_pending_billing && (
                        <Link
                          to={`/billings/add/${site.id}`}
                          className="flex gap-1 items-center justify-center text-xs bg-green-100 rounded-full text-green-600 hover:text-green-800 px-2 py-0.5 hover:bg-green-50 transition-colors"
                          title="Add billing"
                        >
                          <FaCreditCard /> Add Billing
                        </Link>
                      )}

                      {canAssignEngineer(site) && (
                        <button
                          onClick={() => handleAssignment('engineer', site)}
                          className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded transition-colors"
                          title="Assign Engineer"
                          disabled={actionLoading}
                        >
                          <FaUserCheck className="w-4 h-4" />
                        </button>
                      )}

                      {canAssignReportMaker(site) && (
                        <button
                          onClick={() => handleAssignment('reportMaker', site)}
                          className="text-purple-600 hover:text-purple-800 p-2 hover:bg-purple-50 rounded transition-colors"
                          title="Assign Report Maker"
                          disabled={actionLoading}
                        >
                          <FaFileAlt className="w-4 h-4" />
                        </button>
                      )}

                      {canAssignValidator(site) && (
                        <button
                          onClick={() => handleAssignment('validator', site)}
                          className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded transition-colors"
                          title="Assign Validator"
                          disabled={actionLoading}
                        >
                          <FaCheckCircle className="w-4 h-4" />
                        </button>
                      )}

                      {canHoldSite(site) && (
                        <button
                          onClick={() => handleAction('hold', site)}
                          className="text-amber-600 hover:text-amber-800 p-2 hover:bg-amber-50 rounded transition-colors"
                          title="Put on Hold"
                          disabled={actionLoading}
                        >
                          <FaPause className="w-4 h-4" />
                        </button>
                      )}

                      {canCancelSite(site) && (
                        <button
                          onClick={() => handleAction('cancel', site)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Cancel Site"
                          disabled={actionLoading}
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Site ID */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {site.site_id}
                      </span>
                    </div>
                  </td>

                  {/* Project Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.project?.project_name || <span className="text-gray-400 italic">No Project</span>}
                      </span>
                    </div>
                  </td>

                  {/* Client */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.project?.client?.client_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Activity */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.project?.activity?.activity_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Engineer */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.site_engineer ?
                          `${site.site_engineer.first_name} ${site.site_engineer.last_name}` :
                          <span className="text-gray-400 italic">Not Assigned</span>
                        }
                      </span>
                    </div>
                  </td>

                  {/* Technical Associate */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.ta ?
                          `${site.ta.first_name} ${site.ta.last_name}` :
                          <span className="text-gray-400 italic">Not Assigned</span>
                        }
                      </span>
                    </div>
                  </td>

                  {/* Report Maker */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.report_designer ?
                          `${site.report_designer.first_name} ${site.report_designer.last_name}` :
                          <span className="text-gray-400 italic">Not Assigned</span>
                        }
                      </span>
                    </div>
                  </td>

                  {/* Validator */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {site.validator ?
                          `${site.validator.first_name} ${site.validator.last_name}` :
                          <span className="text-gray-400 italic">Not Assigned</span>
                        }
                      </span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getStatusBadge(site.survey_workflow_status)}
                  </td>

                  {/* Visit Remarks */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {site.visit_remarks ? (
                        <span title={site.visit_remarks} className="truncate max-w-[150px] block">
                          {site.visit_remarks}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">No remarks</span>
                      )}
                    </span>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {formatDate(site.created_at)}
                      </span>
                    </div>
                  </td>

                  {/* Updated At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {formatDate(site.updated_at)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Assignment Modal */}
      {assignmentModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setAssignmentModal({ isOpen: false, type: null, site: null })} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Assign {assignmentModal.type === 'engineer' ? 'Engineer' :
                    assignmentModal.type === 'reportMaker' ? 'Report Maker' : 'Validator'}
                </h3>
                <div className="space-y-3">
                  {getAssignmentUsers().map((user) => (
                    <button
                      key={user.id}
                      onClick={() => executeAssignment(user.id)}
                      className="w-full text-left p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      disabled={actionLoading}
                    >
                      <div className="font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </button>
                  ))}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setAssignmentModal({ isOpen: false, type: null, site: null })}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Action Modal (Cancel/Hold) */}
      {actionModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setActionModal({ isOpen: false, type: null, site: null, reason: '' })} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {actionModal.type === 'cancel' ? 'Cancel Site' : 'Put Site on Hold'}
                </h3>
                <p className="text-gray-600 mb-4">
                  Please provide a reason for {actionModal.type === 'cancel' ? 'cancelling' : 'putting on hold'} this site.
                </p>
                <textarea
                  value={actionModal.reason}
                  onChange={(e) => setActionModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Enter reason..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                />
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setActionModal({ isOpen: false, type: null, site: null, reason: '' })}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeAction}
                    disabled={!actionModal.reason.trim() || actionLoading}
                    className={`px-4 py-2 rounded text-white font-medium transition-colors ${actionModal.type === 'cancel'
                      ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                      : 'bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300'
                      }`}
                  >
                    {actionModal.type === 'cancel' ? 'Cancel Site' : 'Put on Hold'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="project_sites"
        title="Export Sites Data"
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
          setFilters(advancedFilters);
        }}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        projects={projects}
        activities={activities}
      />
    </div>
  );
};

export default BillingSites;
